import { useState, useEffect, useMemo, useRef } from "react";

// service
import "./service/ChartSetup";
// Helpers
import {
  dataLabelsPlugin,
  mapppingColorForBGBadgeRole,
} from "./helpers/constants";
import { buildLoginStats, fetchStatsData } from "./helpers/loginStats";
import { buildPagination } from "./helpers/pagination";
import { buildAnnualUserLogins } from "./helpers/annualLoginStats";
import { buildTrendChartData } from "./helpers/chartBuilders";
import { trendChartOptions } from "./helpers/chartOptions";

//  Hooks
import { useFetchLoginHistories } from "./hooks/fetchData";

// Components
import MasterDataAccordion from "./components/MasterDataAccordion.js";
import LeaderboardAccordion from "./components/LeaderboardAccordion.js";
import StatisticsAccordion from "./components/StatisticsAccordion.js";
import AnnualLoginAccordion from "./components/AnnualLoginHistory.js";

const LoginHistory = () => {
  // --- STATE TABLE ---
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPerPage, setCurrentPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [usersLogins, setUsersLogins] = useState([]);
  // STATE FILTER
  const [searchName, setSearchName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [annualPage, setAnnualPage] = useState(1);
  const [annualPerPage, setAnnualPerPage] = useState(10);

  // --- STATE CHART & STATS ---
  const [chartDataRaw, setChartDataRaw] = useState([]);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("daily");
  const [dataUser, setDataUser] = useState([]);
  const chartRef = useRef(null);
  useEffect(() => {
    setUsersLogins(buildAnnualUserLogins(chartDataRaw));
    setDataUser(chartDataRaw);
  }, [chartDataRaw]);

  const { loginHistories, fetch } = useFetchLoginHistories(
    isLoading,
    setIsLoading,
    totalPages,
    setTotalPages
  );

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      if (!isMounted) return;
      await fetch({
        currentPage,
        currentPerPage,
        searchName,
        startDate,
        endDate,
        selectedRole,
      });
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [
    currentPage,
    currentPerPage,
    searchName,
    startDate,
    endDate,
    selectedRole,
  ]);

  useEffect(() => {
    fetchStatsData(setIsStatsLoading, setChartDataRaw);
  }, []);

  const { topUsersList, roleStats, trendStats } = useMemo(() => {
    if (!chartDataRaw.length) {
      return {
        topUsersList: [],
        roleStats: { labels: [], data: [] },
        trendStats: {
          daily: { labels: [], data: [] },
          weekly: { labels: [], data: [] },
        },
      };
    }

    return buildLoginStats(chartDataRaw);
  }, [chartDataRaw]);

  const trendChartData = useMemo(
    () => buildTrendChartData(trendStats, timeRange),
    [trendStats, timeRange]
  );

  const roleChartData = {
    labels: roleStats?.labels || [],
    datasets: [
      {
        data: roleStats?.data || [],
        backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"],
        borderWidth: 1,
        hoverOffset: 30,
        hoverBorderWidth: 2,
        hoverBorderColor: "#ffffff",
      },
    ],
  };

  const chartPlugins = useMemo(() => [dataLabelsPlugin], []);

  const handleResetFilter = () => {
    setSearchName("");
    setStartDate("");
    setEndDate("");
    setSelectedRole("");
    setCurrentPage(1);
    setAnnualPage(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setIsLoading(true);
    setCurrentPage(page);
  };

  // Pagination Logic
  const offset = (currentPage - 1) * currentPerPage;
  const pageNumbers = useMemo(
    () => buildPagination(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const sortedAnnualUsers = useMemo(() => {
    return [...usersLogins].sort(
      (a, b) => Number(b.jumlahLogin ?? 0) - Number(a.jumlahLogin ?? 0)
    );
  }, [usersLogins]);

  const annualTotalPages = useMemo(() => {
    return Math.ceil(sortedAnnualUsers.length / annualPerPage) || 1;
  }, [sortedAnnualUsers.length, annualPerPage]);

  useEffect(() => {
    if (annualPage > annualTotalPages) setAnnualPage(annualTotalPages);
  }, [annualPage, annualTotalPages]);

  const annualOffset = (annualPage - 1) * annualPerPage;

  const annualPaginated = useMemo(() => {
    return sortedAnnualUsers.slice(annualOffset, annualOffset + annualPerPage);
  }, [sortedAnnualUsers, annualOffset, annualPerPage]);

  const handleAnnualPageChange = (page) => {
    if (page < 1 || page > annualTotalPages || page === annualPage) return;
    setAnnualPage(page);
  };

  // Accordion Configuration
  const accordionConfig = [
    {
      id: "leaderboard",
      title: "Top 10 Leaderboard",
      icon: "fa-trophy",
      className: "accordion-item border-0",
      component: LeaderboardAccordion,
      props: {
        isStatsLoading,
        topUsersList,
      },
    },
    {
      id: "statistics",
      title: "Statistik & Riwayat Data",
      icon: "fa-chart-line",
      className: "accordion-item border-0 border-top",
      component: StatisticsAccordion,
      props: {
        isStatsLoading,
        isExpanded,
        setIsExpanded,
        timeRange,
        setTimeRange,
        trendChartData,
        roleChartData,
        chartPlugins,
        trendChartOptions,
        chartRef,
        isLoading,
        loginHistories,
        offset,
        totalPages,
        searchName,
        setSearchName,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        currentPage,
        setCurrentPage,
        currentPerPage,
        setCurrentPerPage,
        pageNumbers,
        handlePageChange,
        handleResetFilter,
        mapppingColorForBGBadgeRole,
        selectedRole,
        setSelectedRole,
      },
    },
    {
      id: "annual",
      title: "Riwayat Login Tahunan",
      icon: "fa-table",
      className: "accordion-item border-0 border-top",
      component: AnnualLoginAccordion,
      props: {
        dataUser,
        searchName,
        setSearchName,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        annualPage,
        annualPerPage,
        setAnnualPage,
        setAnnualPerPage,
        annualTotalPages,
        annualOffset,
        annualPaginated,
        isLoading,
        handleResetFilter,
        handleAnnualPageChange,
        mapppingColorForBGBadgeRole,
        selectedRole,
        setSelectedRole,
      },
    },
  ];

  return (
    <>
      <div className="container mt-4 mb-5">
        <h3 className="mb-4 fw-bold text-dark">Dashboard Monitoring</h3>

        {/* START ACCORDION */}
        <MasterDataAccordion accordionConfig={accordionConfig} />
        {/* END ACCORDION */}
      </div>
    </>
  );
};

export default LoginHistory;
